/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
// import cn from "classnames";
// import Stack from "../Stack";
// import s from "./Input.module.css";
// const Select = ({
//   options,
//   onChange,
//   className,
//   value,
//   isRequired,
//   disabled,
//   isLoading,
//   variant,
//   isPreview,
//   previewData = "",
//   ...rest
// }) => {
//   if (isPreview)
//     return (
//       <div className="text-font-primary text-xl">
//         {previewData.length > 0 ? previewData : value}
//       </div>
//     );

//   const handleOnChange = (e) => {
//     e.currentTarget.blur();

//     if (onChange) {
//       onChange(e);
//     }
//   };
//   const hasNoSelects = value === "" || value === undefined || value === null;
//   return (
//     <Stack className="relative">
//       <select
//         className={cn("outline-0 border -border--component-colors-field-default-input-border bg-transparent -text--component-colors-field-default-input-text w-full h-[48px] px-3", s.selectInput, className, variant && s[variant], {
//           "cursor-not-allowed": disabled,
//         })}
//         onChange={handleOnChange}
//         disabled={disabled}
//         value={hasNoSelects ? "" : value}
//         {...rest}
//       >
//         {!isRequired && (
//           <option
//             id={`0`}
//             disabled
//             value=""
//             data-default-option="true"
//             className="text-font-primary -bg--theme-colors-surface-neutral-level02"
//           >
//             Select an option
//           </option>
//         )}
//         {Array.isArray(options) &&
//           options.length > 0 &&
//           options.map(({ id, value, label, childClassName }, i) => {
//             return (
//               <option
//                 key={id || i}
//                 id={`${id}`}
//                 value={value}
//                 className={childClassName}
//               >
//                 {label || value}
//               </option>
//             );
//           })}
//       </select>
//     </Stack>
//   );
// };

// export default Select;
import classNames from "classnames";
import { cva } from "cva";
import { ChangeEvent, InputHTMLAttributes, useRef } from "react";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { Label, QuestionIcon, Text } from "..";
import { useTranslation } from "react-i18next";

type Option = {
  id?: string | number;
  value: string;
  label?: string;
  childClassName?: string;
};

export type BaseProps = {
  name?: string;
  value?: string;
  label?: string;
  inputClassName?: string;
  labelClassName?: string;
  tooltip?: string;
  error?: boolean;
  locked?: boolean;
  options: Option[];
  isPreview?: boolean;
};

type Props = BaseProps &
  Omit<InputHTMLAttributes<HTMLSelectElement>, keyof BaseProps> & {
    onChange?: (e: ChangeEvent<HTMLSelectElement>) => void | Function;
  };

const SelectStyles = cva(
  "w-full px-3 py-2.5 leading-6 focus:outline-none focus:ring-0 border bg-inherit cursor-pointer appearance-none",
  {
    variants: {
      state: {
        default: [
          "-border--component-colors-field-default-input-border -text--component-colors-field-filled-input-text placeholder:-text--component-colors-field-default-input-placeholder",
          "disabled:-border--component-colors-field-disabled-input-border placeholder:-text--component-colors-field-disabled-input-placeholder",
          "hover:-border--component-colors-field-hover-input-border -text--component-colors-field-hover-input-text placeholder:-text--component-colors-field-hover-input-placeholder",
          "focus:-border--component-colors-field-selected-input-border  -rounded-input",
        ],
        error:
          "-border--component-colors-field-error-input-border -text--component-colors-field-error-input-text placeholder:-text--component-colors-field-error-input-placeholder",
        locked:
          "-border--component-colors-field-locked-input-border -text--component-colors-field-locked-input-text placeholder:-text--component-colors-field-locked-input-placeholder",
      },
    },
    defaultVariants: {
      state: "default",
    },
  },
);

const Select = (props: Props) => {
  const {
    type,
    placeholder,
    className,
    inputClassName,
    labelClassName,
    label,
    error,
    locked,
    options,
    isPreview,
    ...rest
  } = props;
  const selectRef = useRef<HTMLSelectElement>(null);
  const { t } = useTranslation();
  return (
    <div className={classNames("w-full flex flex-col", className)}>
      <div className="w-full flex items-center justify-between ">
        <Label
          isPreview={isPreview}
          locked={locked}
          error={error}
          htmlFor={props.id}
          required={props.required}
          className={labelClassName}
        >
          {label}
        </Label>
        {props.tooltip && (
          <div className="flex items-center justify-center cursor-pointer">
            <span
              data-tooltip-id={`${label}-${type}`}
              data-tooltip-content={props.tooltip}
            >
              <QuestionIcon width={20} height={20} />
            </span>
            <Tooltip
              place={"right"}
              className="bg-gray-800 max-w-[180px] text-xs text-white leading-6 p-1"
              id={`${label}-${type}`}
            />
          </div>
        )}
      </div>
      {isPreview ? (
        <Text className="-text--theme-colors-text-body text-xl">
          {options?.find((option) => option.value === props.value)?.label ||
            props.value ||
            placeholder ||
            "-"}
        </Text>
      ) : (
        <div className="flex items-center w-full relative">
          <select
            ref={selectRef}
            {...(rest as Partial<Props>)}
            className={classNames(
              SelectStyles({
                state: (error && "error") || (locked && "locked") || "default",
              }),
              { "mt-2.5": label },
              inputClassName,
            )}
          >
            {props.required && (
              <option
                id={`0`}
                disabled
                value=""
                data-default-option="true"
                className="-text--component-colors-field-default-input-text -bg--theme-colors-surface-neutral-level02 my-4"
              >
                {t("forms.placeholders.selectOption")}
              </option>
            )}
            {Array.isArray(options) &&
              options.map(({ id, value, label, childClassName }, i) => {
                return (
                  <option
                    key={id || i}
                    id={id?.toString()}
                    value={value}
                    className={classNames(
                      "-text--component-colors-field-default-input-text -bg--theme-colors-surface-neutral-level02 my-4",
                      childClassName,
                    )}
                  >
                    {label || value}
                  </option>
                );
              })}
          </select>
        </div>
      )}
    </div>
  );
};

export default Select;
